import type { CalendarGridEvent, LaidOutCalendarEvent } from '@/types/calendar/grid'

/** Day-grid hour cell height in px (matches Tailwind h-14). */
export const CALENDAR_HOUR_HEIGHT_PX = 56
export const CALENDAR_PX_PER_MIN = CALENDAR_HOUR_HEIGHT_PX / 60
export const CALENDAR_MIN_VISUAL_DURATION_MIN = 15
export const CALENDAR_MIN_EVENT_HEIGHT_PX = CALENDAR_MIN_VISUAL_DURATION_MIN * CALENDAR_PX_PER_MIN

export const CALENDAR_HOURS = Array.from({ length: 24 }, (_, i) => i)

export const formatCalendarHour = (h: number): string => {
  if (h === 0) return '00:00'
  return `${h.toString().padStart(2, '0')}:00`
}

/**
 * Lay out overlapping events into side-by-side columns (prototype algorithm).
 */
export function layoutDayEvents(events: CalendarGridEvent[]): LaidOutCalendarEvent[] {
  const sorted = [...events].sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin
    return b.durationMin - a.durationMin
  })
  const result: LaidOutCalendarEvent[] = []
  let cluster: LaidOutCalendarEvent[] = []
  let clusterEnd = -Infinity
  let columns: number[] = []

  const flush = (): void => {
    if (cluster.length === 0) return
    const cols = columns.length
    for (const ev of cluster) ev.cols = cols
    result.push(...cluster)
    cluster = []
    columns = []
    clusterEnd = -Infinity
  }

  for (const ev of sorted) {
    const start = ev.startMin
    const end = start + ev.durationMin
    if (start >= clusterEnd) flush()

    let assigned = -1
    for (let i = 0; i < columns.length; i++) {
      if ((columns[i] ?? -1) <= start) {
        assigned = i
        columns[i] = end
        break
      }
    }
    if (assigned === -1) {
      assigned = columns.length
      columns.push(end)
    }
    cluster.push({ ...ev, col: assigned, cols: 0 })
    clusterEnd = Math.max(clusterEnd, end)
  }
  flush()
  return result
}

export function eventChipStyle(
  ev: LaidOutCalendarEvent,
  rightPx = 8
): { top: string; height: string; left: string; width: string } {
  const gap = 2
  const totalReserved = (ev.cols - 1) * gap + rightPx
  const colWidth = `((100% - ${totalReserved}px) / ${ev.cols})`
  return {
    top: `${ev.startMin * CALENDAR_PX_PER_MIN}px`,
    height: `${Math.max(CALENDAR_MIN_EVENT_HEIGHT_PX, ev.durationMin * CALENDAR_PX_PER_MIN)}px`,
    left: `calc(${ev.col} * (${colWidth} + ${gap}px))`,
    width: `calc(${colWidth})`
  }
}

export const isCompactCalendarEvent = (ev: CalendarGridEvent): boolean =>
  ev.durationMin * CALENDAR_PX_PER_MIN < 36

export const isUltraCompactCalendarEvent = (ev: CalendarGridEvent): boolean =>
  ev.durationMin * CALENDAR_PX_PER_MIN < 24
